import React from 'react';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { setFormData } from '@plone/volto/actions/form/form';
import { getBaseUrl, flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import config from '@plone/volto/registry';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import clearSVG from '@plone/volto/icons/clear.svg';
import addSVG from '@plone/volto/icons/add.svg';
import backSVG from '@plone/volto/icons/back.svg';
import clockSVG from '@plone/volto/icons/clock.svg';
import {
  addAssistantMessage,
  appendAssistantToolCall,
  cancelAssistantMessage,
  completeAssistantMessage,
  loadAssistantConversation,
  resetAssistantConversation,
  setAssistantDraft,
  setAssistantFloatingOpen,
  setAssistantPendingFocus,
  setAssistantPendingRetry,
  setAssistantPermission,
  setAssistantFollowChanges,
  streamAssistantMessage,
} from '../../actions/assistant';
import type { HttpError } from '../../api/HttpTransport';
import { agentClient, canonicalPagePathname } from '../../api/client';
import type {
  Attachment,
  MessageContext,
  Permission,
  Reference,
  Skill,
  SkillGroup,
} from '../../types';
import {
  moveCursor,
  clearCursor,
  clearAgentMarks,
  flashTextChange,
  fadeImageSwap,
  animateReorder,
  indexColumnNodes,
} from './cursor';
import { changedOrderIds, collectBlockIds, findBlock } from './blockOrder';
import {
  hydrateConversationMessages,
  titleFromMessages,
} from './conversationTranscript';
import AssistantMessage from './AssistantMessage';
import Composer from './Composer';
import ConversationHistory from './ConversationHistory';
import './assistant.css';

/** What a caller may override when sending a turn. */
interface SendOptions {
  permission?: Permission;
  attachments?: Attachment[];
  references?: Reference[];
  skills?: Skill[];
  context?: MessageContext | null;
}

/**
 * A denied turn parked until the edit form is up — the same four fields,
 * with the text that was refused.
 */
interface PendingRetry extends SendOptions {
  text: string;
}

const messages = defineMessages({
  assistant: {
    id: 'Assistant',
    defaultMessage: 'Assistant',
  },
  newConversation: {
    id: 'New conversation',
    defaultMessage: 'New conversation',
  },
  close: {
    id: 'Close',
    defaultMessage: 'Close',
  },
  conversations: {
    id: 'assistantConversations',
    defaultMessage: 'Conversations',
  },
  conversationHistory: {
    id: 'assistantConversationHistory',
    defaultMessage: 'Open conversation history',
  },
  backToConversation: {
    id: 'assistantBackToConversation',
    defaultMessage: 'Back to conversation',
  },
  emptyTitle: {
    id: 'assistantEmptyTitle',
    defaultMessage: 'What should we do with this page?',
  },
  emptyHint: {
    id: 'assistantEmptyHint',
    defaultMessage:
      'Edits land in the working copy — nothing is published until you save.',
  },
  suggestionStructure: {
    id: 'assistantSuggestionStructure',
    defaultMessage: 'What is on this page?',
  },
  suggestionSection: {
    id: 'assistantSuggestionSection',
    defaultMessage: 'Add a section about our opening hours',
  },
  suggestionMakeover: {
    id: 'assistantSuggestionMakeover',
    defaultMessage: 'Give this page a makeover',
  },
  suggestionMove: {
    id: 'assistantSuggestionMove',
    defaultMessage: 'Move this block to the top',
  },
});

const newId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `msg-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

const isEditRoute = (pathname) =>
  pathname.endsWith('/edit') || pathname.endsWith('/add');

const EMPTY_DRAFT = { text: '', attachments: [], references: [], skills: [] };

// The one running job. Module-scoped, not a component ref: the panel can
// unmount and remount mid-stream (floating panel closed and reopened) and
// Cancel must still reach the job.
const activeJob = { current: null }; // { job, uid, pathname }

const stopActiveJob = () => {
  if (!activeJob.current) return null;
  const { job, uid } = activeJob.current;
  activeJob.current = null;
  job.cancel();
  clearAgentMarks();
  return uid;
};

/**
 * The assistant chat panel. Hosted either as a tab in the edit-mode sidebar
 * (host="sidebar") or as a floating dock in view mode (host="floating").
 *
 * The working copy is the integration point: in edit mode the page state
 * sent to the (mock) agent is the live form data from redux, and every
 * mutating tool step carries the state after that step, which is applied
 * back via setFormData as it happens — the canvas changes in real time
 * while the agent works, each touched block flashing, and everything stays
 * undoable/cancelable through Volto's own editing gates.
 *
 * Every stream callback is scoped to the turn it belongs to: it bails when
 * the job was superseded (cancel, reset, a newer turn) and the whole job is
 * cancelled when the page changes under it or its host unmounts — a turn
 * started on one page can never write into another page's form.
 */
const AssistantPanel = ({ host }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();

  const assistant = useAppSelector((state) => state.assistant);
  const formGlobal = useAppSelector((state) => state.form?.global);
  const selectedBlock = useAppSelector((state) => state.form?.ui?.selected);
  const contentData = useAppSelector((state) => state.content?.data);

  const inEdit = isEditRoute(location.pathname);
  const basePathname = canonicalPagePathname(getBaseUrl(location.pathname));
  const pageState = inEdit ? formGlobal : contentData;

  const [historyOpen, setHistoryOpen] = React.useState(false);
  const [skills, setSkills] = React.useState<Skill[]>([]);
  const [skillGroups, setSkillGroups] = React.useState<SkillGroup[]>([]);
  const [conversations, setConversations] = React.useState([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);
  const [historyError, setHistoryError] = React.useState(null);
  const [conversationBusyId, setConversationBusyId] = React.useState(null);

  const loadConversations = React.useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      setConversations(
        await agentClient().listConversations({ pathname: basePathname }),
      );
    } catch (error) {
      setHistoryError(error);
    } finally {
      setHistoryLoading(false);
    }
  }, [basePathname]);

  React.useEffect(() => {
    setHistoryOpen(false);
    loadConversations();
  }, [loadConversations]);

  // Once per panel: the catalogue is a property of the agent, not of the page.
  // A backend that offers none, or cannot be reached, leaves the picker away;
  // one whose groups cannot be read still lists every skill at the top level.
  React.useEffect(() => {
    let live = true;
    const client = agentClient();
    client
      .listSkills()
      .then((available) => live && setSkills(available))
      .catch(() => live && setSkills([]));
    client
      .listSkillGroups()
      .then((available) => live && setSkillGroups(available))
      .catch(() => live && setSkillGroups([]));

    return () => {
      live = false;
    };
  }, []);

  const nestedSelection = React.useMemo(
    () => findBlock(pageState, assistant.contextBlockId),
    [pageState, assistant.contextBlockId],
  );
  const topLevelSelection = React.useMemo(
    () => findBlock(pageState, selectedBlock),
    [pageState, selectedBlock],
  );
  const nestedSelectionIsCurrent =
    nestedSelection &&
    (nestedSelection.id === selectedBlock ||
      nestedSelection.rootId === selectedBlock);
  const activeSelection = nestedSelectionIsCurrent
    ? nestedSelection
    : topLevelSelection;
  const activeBlockId = activeSelection?.id || null;

  const [dismissedBlock, setDismissedBlock] = React.useState(null);
  React.useEffect(() => {
    setDismissedBlock(null);
  }, [activeBlockId]);

  // Unsent composer input lives in the store (it survives the write
  // toggle navigating into edit mode). References are managed here rather
  // than in the composer because the object browser HOC wrapping the
  // composer reads the selection array from the composer's props.
  const draft = assistant.draft ?? EMPTY_DRAFT;
  const onChangeDraft = React.useCallback(
    (patch) => dispatch(setAssistantDraft(patch)),
    [dispatch],
  );
  const onToggleReference = (url, item) => {
    const id = flattenToAppURL(url);
    const references = draft.references ?? [];
    onChangeDraft({
      references: references.some((reference) => reference['@id'] === id)
        ? references.filter((reference) => reference['@id'] !== id)
        : [
            ...references,
            { '@id': id, title: item.title || id, type: item['@type'] },
          ],
    });
  };

  // Picked skills live in the draft for the same reason: they must outlive
  // the composer remount.
  const onAddSkill = (skill: Skill) => {
    const picked = draft.skills ?? [];
    if (picked.some((candidate) => candidate.name === skill.name)) return;
    onChangeDraft({ skills: [...picked, skill] });
  };
  const onRemoveSkill = (name: string) =>
    onChangeDraft({
      skills: (draft.skills ?? []).filter((skill) => skill.name !== name),
    });

  const scrollRef = React.useRef(null);
  const messageCountRef = React.useRef(0);
  // Full form state as of the last applied tool step. Container blocks own
  // nested layouts, so top-level `blocks_layout` alone cannot recognize a
  // moved column or a block inserted inside one.
  const appliedStateRef = React.useRef(null);
  // Guards the async gap in onSend against a double-send.
  const sendingRef = React.useRef(false);

  // Default at read time: SSR-serialized state may predate this key.
  const followChanges = assistant.followChanges ?? true;

  // Read at tool-call time (the job callbacks outlive this render).
  const followRef = React.useRef(true);
  followRef.current = followChanges;
  const pathnameRef = React.useRef(basePathname);
  pathnameRef.current = basePathname;

  // A job must not outlive its host: without a panel there is no Stop
  // button and no transcript — an orphan would keep mutating the page.
  React.useEffect(
    () => () => {
      const uid = stopActiveJob();
      if (uid) dispatch(cancelAssistantMessage(uid));
    },
    [dispatch],
  );

  // Follow the conversation: hard-scroll on new messages, soft-follow
  // streaming updates unless the user scrolled up to read.
  React.useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const isNewMessage = assistant.messages.length !== messageCountRef.current;
    messageCountRef.current = assistant.messages.length;
    const nearBottom =
      node.scrollHeight - node.scrollTop - node.clientHeight < 120;
    if (isNewMessage || nearBottom) {
      node.scrollTop = node.scrollHeight;
    }
  }, [assistant.messages]);

  const contextBlockData =
    inEdit && selectedBlock && activeBlockId !== dismissedBlock
      ? activeSelection?.block
      : null;
  const contextBlockConfig = contextBlockData
    ? config.blocks.blocksConfig[contextBlockData['@type']]
    : null;
  const contextBlock = contextBlockData
    ? {
        id: activeBlockId,
        type: contextBlockData['@type'],
        title: contextBlockConfig?.title || contextBlockData['@type'],
        icon: contextBlockConfig?.icon || null,
      }
    : null;

  const onSetPermission = (permission) => {
    dispatch(setAssistantPermission(permission));
    // In view mode, granting write routes into editing first, so that
    // mutations always land in a working copy the user reviews and saves.
    if (permission === 'write' && !inEdit) {
      dispatch(setAssistantPendingFocus(true));
      history.push(`${basePathname === '/' ? '' : basePathname}/edit`);
    }
  };

  const onSend = async (text: string, options: SendOptions = {}) => {
    if (assistant.running || sendingRef.current) return;
    sendingRef.current = true;
    try {
      const permission = options.permission || assistant.permission;
      const attachments = options.attachments || [];
      const references = options.references ?? draft.references ?? [];
      const skills = options.skills ?? draft.skills ?? [];
      if (options.references == null) onChangeDraft({ references: [] });
      if (options.skills == null) onChangeDraft({ skills: [] });
      // The block the request is about: the current selection, unless a
      // retry replays the selection of the original attempt.
      const context =
        options.context !== undefined
          ? options.context
          : contextBlock
            ? { block_id: contextBlock.id }
            : null;

      let conversationId = assistant.conversationId;
      if (!conversationId || assistant.pathname !== basePathname) {
        try {
          const created = await agentClient().createConversation({
            state: pageState,
            pathname: basePathname,
            language: intl.locale === 'de' ? 'de' : 'en',
          });
          conversationId = created.conversationId;
          dispatch(resetAssistantConversation(conversationId, basePathname));
        } catch (error) {
          // No conversation, no turn — show the attempt and what went wrong.
          dispatch(
            addAssistantMessage({
              uid: newId(),
              role: 'user',
              content: text,
              attachments,
              references,
              skills,
              parts: [],
              state: null,
              before: null,
              meta: null,
              pathname: basePathname,
              status: 'done',
            }),
          );
          dispatch(
            addAssistantMessage({
              uid: newId(),
              role: 'assistant',
              parts: [],
              state: null,
              before: null,
              meta: {
                error: {
                  kind: (error as HttpError).kind || 'unreachable',
                  detail: (error as Error).message,
                },
              },
              pathname: basePathname,
              status: 'done',
            }),
          );
          return;
        }
      }

      dispatch(
        addAssistantMessage({
          uid: newId(),
          role: 'user',
          content: text,
          attachments,
          references,
          skills,
          parts: [],
          state: null,
          before: null,
          meta: null,
          pathname: basePathname,
          status: 'done',
        }),
      );
      const assistantUid = newId();
      dispatch(
        addAssistantMessage({
          uid: assistantUid,
          role: 'assistant',
          // Ordered turn transcript: text segments and tool calls, exactly
          // as the agent emitted them.
          parts: [],
          state: null,
          // Snapshot of the page before this turn ran — "Undo this change"
          // applies it back.
          before: pageState,
          meta: null,
          pathname: basePathname,
          status: 'running',
        }),
      );

      // Every callback of this job answers two questions first: is this
      // still the active job (not cancelled, reset, or superseded), and is
      // the page it was started on still the page on screen? A page change
      // kills the job — its states describe the old page.
      const sentPathname = basePathname;
      const sentInEdit = inEdit;
      const live = () => {
        if (activeJob.current?.uid !== assistantUid) return false;
        if (pathnameRef.current !== sentPathname) {
          stopActiveJob();
          dispatch(cancelAssistantMessage(assistantUid));
          return false;
        }
        return true;
      };

      appliedStateRef.current = pageState;
      // An explicit location request keeps its brief highlight while the
      // answer streams; the cursor's own timer clears it afterwards.
      let explicitlyFocused = false;
      const job = agentClient().sendMessage({
        message: text,
        state: pageState,
        context,
        attachments,
        references,
        skills,
        permission,
        onToolCall: (toolCall) => {
          if (!live()) return;
          dispatch(
            appendAssistantToolCall(assistantUid, {
              name: toolCall.name,
              description: toolCall.description,
              blocked: !!toolCall.blocked,
              // The tool raised, or was stood down because an earlier call
              // in the same step did. Either way it changed nothing.
              failed: !!toolCall.failed,
              blockId: toolCall.blockId ?? null,
              before: toolCall.before ?? null,
              after: toolCall.after ?? null,
              image: toolCall.image ?? null,
              // Whether this step's state landed on the canvas — a
              // cancelled turn offers undo only if something was applied.
              applied: !!toolCall.state && sentInEdit,
              state: null,
            }),
          );
          if (!sentInEdit) return;
          if (toolCall.name === 'focus') {
            if (
              !toolCall.failed &&
              !toolCall.blocked &&
              toolCall.blockId &&
              collectBlockIds(appliedStateRef.current).has(toolCall.blockId)
            ) {
              explicitlyFocused = true;
              indexColumnNodes(appliedStateRef.current);
              moveCursor(toolCall.blockId, { follow: true, focus: true });
            }
            return;
          }
          // Live canvas update: apply the state after this step immediately.
          const previousState = appliedStateRef.current;
          const entered =
            !!toolCall.state &&
            !!toolCall.blockId &&
            !collectBlockIds(previousState).has(toolCall.blockId);
          if (toolCall.state) {
            // Swapped pictures cross-fade: the old image must be snapshotted
            // while it is still in the DOM, before the new state renders.
            if (toolCall.image && toolCall.blockId) {
              fadeImageSwap(toolCall.blockId);
            }
            // Order changes glide (FLIP): displaced blocks animate from
            // their old position; the block a move step targeted travels
            // with the "picked up" treatment.
            const changedIds = changedOrderIds(previousState, toolCall.state);
            if (changedIds.size) {
              animateReorder({
                beforeState: previousState,
                afterState: toolCall.state,
                blockIds: changedIds,
                liftId:
                  toolCall.name === 'move_element' ? toolCall.blockId : null,
              });
            }
            appliedStateRef.current = toolCall.state;
            dispatch(setFormData(toolCall.state));
          }
          // The cursor follows every step that names a block — also
          // read-only ones, so sweeps read as the agent walking the page.
          if (toolCall.blockId) {
            explicitlyFocused = false;
            moveCursor(toolCall.blockId, {
              follow: followRef.current,
              entered,
            });
            if (toolCall.before != null && toolCall.after != null) {
              flashTextChange(
                toolCall.blockId,
                toolCall.before,
                toolCall.after,
              );
            }
          }
        },
        onChunk: (content, segment) => {
          if (!live()) return;
          // The agent stepped back and is talking — whether this is mid-run
          // narration or the final reply is unknowable until the next event.
          if (sentInEdit && !explicitlyFocused) clearCursor();
          dispatch(streamAssistantMessage(assistantUid, content, segment));
        },
        onDone: ({ newState, denied, error }) => {
          if (!live()) return;
          activeJob.current = null;
          if (sentInEdit && (!explicitlyFocused || error)) clearCursor();
          dispatch(
            completeAssistantMessage(
              assistantUid,
              newState,
              denied
                ? {
                    denied: true,
                    retryText: text,
                    retryAttachments: attachments,
                    retryReferences: references,
                    retrySkills: skills,
                    retryContext: context,
                  }
                : error
                  ? { error }
                  : null,
            ),
          );
          if (newState && sentInEdit) {
            dispatch(setFormData(newState));
          }
          loadConversations();
        },
      });
      activeJob.current = { job, uid: assistantUid, pathname: sentPathname };
    } finally {
      sendingRef.current = false;
    }
  };

  const onRetryWrite = (message) => {
    if (!message.meta || assistant.running) return;
    const retry = {
      text: message.meta.retryText || '',
      attachments: message.meta.retryAttachments || [],
      references: message.meta.retryReferences || [],
      skills: message.meta.retrySkills || [],
      // The retry acts on what the denied attempt was about — not on
      // whatever block happens to be selected now.
      context: message.meta.retryContext ?? null,
    };
    dispatch(setAssistantPermission('write'));
    if (!inEdit) {
      // Write turns run against the working copy: route into edit mode
      // first, then send — the parked retry fires once the form is up.
      dispatch(setAssistantPendingRetry(retry));
      dispatch(setAssistantPendingFocus(true));
      history.push(`${basePathname === '/' ? '' : basePathname}/edit`);
      return;
    }
    onSend(retry.text, {
      permission: 'write',
      attachments: retry.attachments,
      references: retry.references,
      skills: retry.skills,
      context: retry.context,
    });
  };

  // A retry parked by the floating panel fires once the edit form is up.
  const pendingRetry = assistant.pendingRetry as PendingRetry | null;
  React.useEffect(() => {
    if (!pendingRetry || !inEdit || assistant.running) return;
    if (!formGlobal?.blocks_layout) return;
    dispatch(setAssistantPendingRetry(null));
    onSend(pendingRetry.text, {
      permission: 'write',
      attachments: pendingRetry.attachments,
      references: pendingRetry.references,
      skills: pendingRetry.skills,
      context: pendingRetry.context,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRetry, inEdit, assistant.running, formGlobal]);

  const onCancel = () => {
    const uid = stopActiveJob();
    if (uid) {
      dispatch(cancelAssistantMessage(uid));
      return;
    }
    // No live job but a message still reads "running" (the job died with
    // its host) — settle the transcript.
    const runningMessage = assistant.messages.find(
      (message) => message.status === 'running',
    );
    if (runningMessage) dispatch(cancelAssistantMessage(runningMessage.uid));
  };

  const onReset = () => {
    // A running turn does not survive its transcript being wiped.
    stopActiveJob();
    dispatch(resetAssistantConversation(null, null));
    setHistoryOpen(false);
  };

  const onOpenHistory = () => {
    setHistoryOpen(true);
    loadConversations();
  };

  const onSelectConversation = async (conversation) => {
    if (assistant.running || conversation.running) return;
    if (
      conversation.id === assistant.conversationId &&
      assistant.messages.length > 0
    ) {
      setHistoryOpen(false);
      return;
    }
    setConversationBusyId(conversation.id);
    setHistoryError(null);
    try {
      const entries = await agentClient().getConversationMessages(
        conversation.id,
      );
      agentClient().activateConversation({
        conversationId: conversation.id,
        pathname: conversation.pathname,
        language: conversation.language || intl.locale,
      });
      dispatch(
        loadAssistantConversation(
          conversation.id,
          conversation.pathname,
          hydrateConversationMessages(entries, conversation.pathname),
        ),
      );
      setHistoryOpen(false);
    } catch (error) {
      setHistoryError(error);
      loadConversations();
    } finally {
      setConversationBusyId(null);
    }
  };

  const onRenameConversation = async (conversationId, title) => {
    setConversationBusyId(conversationId);
    setHistoryError(null);
    try {
      const updated = await agentClient().renameConversation(
        conversationId,
        title,
      );
      setConversations((items) =>
        items.map((item) => (item.id === conversationId ? updated : item)),
      );
      return true;
    } catch (error) {
      setHistoryError(error);
      return false;
    } finally {
      setConversationBusyId(null);
    }
  };

  const onDeleteConversation = async (conversationId) => {
    setConversationBusyId(conversationId);
    setHistoryError(null);
    try {
      await agentClient().deleteConversation(conversationId);
      setConversations((items) =>
        items.filter((item) => item.id !== conversationId),
      );
      if (conversationId === assistant.conversationId) {
        dispatch(resetAssistantConversation(null, null));
      }
      return true;
    } catch (error) {
      setHistoryError(error);
      return false;
    } finally {
      setConversationBusyId(null);
    }
  };

  // "Undo this change" restores the pre-turn snapshot — meaningful only
  // for the newest turn that changed this page (undoing an older one would
  // silently revert everything after it) and only while nothing is
  // running. Cancelled turns count when some steps already landed.
  const undoableUid = React.useMemo(() => {
    for (let i = assistant.messages.length - 1; i >= 0; i--) {
      const message = assistant.messages[i];
      if (message.role !== 'assistant' || !message.before) continue;
      if (message.status === 'done' && message.state) return message.uid;
      if (
        message.status === 'cancelled' &&
        message.parts?.some(
          (part) => part.kind === 'tool' && part.toolCall?.applied,
        )
      ) {
        return message.uid;
      }
    }
    return null;
  }, [assistant.messages]);

  const suggestions = [
    intl.formatMessage(messages.suggestionStructure),
    intl.formatMessage(messages.suggestionSection),
    intl.formatMessage(messages.suggestionMakeover),
    ...(contextBlock ? [intl.formatMessage(messages.suggestionMove)] : []),
  ];

  const activeConversation = conversations.find(
    (conversation) => conversation.id === assistant.conversationId,
  );
  const conversationTitle =
    activeConversation?.title ||
    titleFromMessages(assistant.messages) ||
    intl.formatMessage(messages.newConversation);

  return (
    <div className={`assistant-panel assistant-panel--${host}`}>
      <header className="header pulled assistant-conversation-header">
        <button
          type="button"
          className="assistant-conversation-header-title"
          aria-label={intl.formatMessage(
            historyOpen
              ? messages.backToConversation
              : messages.conversationHistory,
          )}
          onClick={historyOpen ? () => setHistoryOpen(false) : onOpenHistory}
        >
          <Icon name={historyOpen ? backSVG : clockSVG} size="24px" />
          <h2>
            {historyOpen
              ? intl.formatMessage(messages.conversations)
              : conversationTitle}
          </h2>
        </button>
        <div className="assistant-panel-actions">
          <button
            type="button"
            aria-label={intl.formatMessage(messages.newConversation)}
            title={intl.formatMessage(messages.newConversation)}
            onClick={onReset}
          >
            <Icon name={addSVG} size="24px" />
          </button>
          {host === 'floating' && (
            <button
              type="button"
              aria-label={intl.formatMessage(messages.close)}
              title={intl.formatMessage(messages.close)}
              onClick={() => dispatch(setAssistantFloatingOpen(false))}
            >
              <Icon name={clearSVG} size="16px" />
            </button>
          )}
        </div>
      </header>
      {historyOpen ? (
        <ConversationHistory
          conversations={conversations}
          activeId={assistant.conversationId}
          loading={historyLoading}
          error={historyError}
          busyId={conversationBusyId}
          onSelect={onSelectConversation}
          onRename={onRenameConversation}
          onDelete={onDeleteConversation}
          onRetry={loadConversations}
        />
      ) : (
        <>
          <div className="assistant-panel-messages" ref={scrollRef}>
            {assistant.messages.length === 0 && (
              <div className="assistant-panel-empty">
                <p className="assistant-panel-empty-title">
                  {intl.formatMessage(messages.emptyTitle)}
                </p>
                <p className="assistant-panel-empty-hint">
                  {intl.formatMessage(messages.emptyHint)}
                </p>
                <div className="assistant-suggestions">
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => onSend(suggestion)}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {assistant.messages.map((message) => (
              <AssistantMessage
                key={message.uid}
                message={message}
                canRestore={inEdit}
                canUndo={
                  inEdit &&
                  !assistant.running &&
                  message.uid === undoableUid &&
                  message.pathname === basePathname
                }
                onRetryWrite={onRetryWrite}
              />
            ))}
          </div>
          <Composer
            pathname={basePathname}
            running={assistant.running}
            permission={assistant.permission}
            onSetPermission={onSetPermission}
            contextBlock={contextBlock}
            onFocusContext={() =>
              moveCursor(contextBlock?.id, { follow: true, transient: true })
            }
            onDismissContext={() => setDismissedBlock(activeBlockId)}
            draft={draft}
            onChangeDraft={onChangeDraft}
            references={draft.references ?? []}
            onToggleReference={onToggleReference}
            pickedSkills={draft.skills ?? []}
            onAddSkill={onAddSkill}
            onRemoveSkill={onRemoveSkill}
            followChanges={followChanges}
            onSetFollowChanges={
              host === 'sidebar'
                ? (next) => dispatch(setAssistantFollowChanges(next))
                : null
            }
            onSend={onSend}
            onCancel={onCancel}
            skills={skills}
            skillGroups={skillGroups}
          />
        </>
      )}
    </div>
  );
};

export default AssistantPanel;
